import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * The "who am I" transition gate for account switching (staff <-> linked parent account, and
 * admin impersonation start/stop).
 *
 * The server flips the identity the moment ClearImpersonation / SwitchToGuardian / Impersonate
 * returns — the dbo.Impersonations row is gone (or replaced) and every later request resolves as
 * the new user. The client, however, keeps running as the old identity for a while afterwards:
 * the switch paths reload the page, and a reload does not stop the current document, so already
 * subscribed HTTP calls stay in flight and components can issue new ones (polling, presence,
 * whatever page is open) until the new document commits (seconds, on a cold mobile bundle).
 * Anything issued in that window arrives at the server as the wrong user: guardian-scoped calls
 * land as staff with no GuardianGUID behind them, and staff-only calls land as the guardian and
 * 403 on their AppAccess_* role gates. Either way it is a pointless call and a logged error.
 *
 * So callers raise the gate BEFORE asking the server to change identity:
 *  - {@link aborted$} fires immediately, unsubscribing app API calls already in flight
 *    (Angular aborts the XHR on unsubscribe);
 *  - {@link isSwitching} stays true so new app API calls are dropped before they are sent.
 * AuthInterceptor exempts the identity-changing calls themselves.
 *
 * It lowers itself again in UserService.setCurrentUserProfile() — the point at which the client
 * has adopted the new profile — or immediately if the identity-changing call itself fails, since
 * then nothing changed. Both releases live in the services that raise it, not in their callers, so
 * no switch path can strand the app with calls blocked for the rest of the session. That matters
 * most on the admin impersonate page, which navigates by router instead of reloading and so has no
 * fresh document to reset the gate.
 *
 * Switch paths that finish with a full page load instead call {@link holdUntilReload}: the old
 * document is still running as the old identity until the new one commits, so the gate must stay
 * up past setCurrentUserProfile(). The reload itself is what lowers it.
 *
 * Deliberately dependency-free (rxjs only) so AuthInterceptor can inject it without a DI cycle,
 * and in-memory only so a page reload always starts with the gate down.
 */
@Injectable({ providedIn: 'root' })
export class IdentitySwitchService {
  private readonly abortSubject = new Subject<void>();
  private switching = false;
  private reloadPending = false;

  /** Whether an identity change is in progress and app API calls should not be issued. */
  get isSwitching(): boolean {
    return this.switching;
  }

  /** Emits once each time a switch begins; used to abort app API calls already in flight. */
  get aborted$(): Observable<void> {
    return this.abortSubject.asObservable();
  }

  /** Raise the gate, and abort whatever is already in flight, before the identity changes. */
  begin(): void {
    this.switching = true;
    this.abortSubject.next();
  }

  /**
   * Keep the gate up until this document is replaced. Only for switch paths that are about to do a
   * full page load, and only once the identity change has succeeded — nothing but the reload lowers
   * it afterwards.
   */
  holdUntilReload(): void {
    this.reloadPending = true;
    this.switching = true;
  }

  /** Lower the gate once the client is running as the new identity (or the switch failed). */
  end(): void {
    if (this.reloadPending) {
      return;
    }
    this.switching = false;
  }
}
